import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ConfirmableDialog from '@/components/common/ConfirmableDialog';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchableTagSelect from '@/components/common/SearchableTagSelect';
import useEntityTagging from '@/components/hooks/useEntityTagging';
import EntityTagInput from '@/components/common/EntityTagInput';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, Shield } from 'lucide-react';

function CollapsibleSection({ title, defaultOpen = true, children, className = "" }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className={`flex items-center gap-2 w-full py-2 text-sm font-semibold text-slate-300 hover:text-white transition-colors ${className}`}>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        {title}
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-3 pt-1 pb-2">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

export default function IssueDialog({ open, onClose, issue, officers, missions, objectives, artifacts, rituals, policies, capabilities, activities, isCommander }) {
  const queryClient = useQueryClient();
  const [currentUserEmail, setCurrentUserEmail] = useState(null);

  useEffect(() => {
    base44.auth.me().then(user => setCurrentUserEmail(user?.email)).catch(() => {});
  }, []);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'execution_headwind',
    severity: 'medium',
    linked_mission_id: '',
    linked_objective_id: '',
    linked_activity_id: '',
    decision: '',
    decision_notes: '',
    observed_impact: '',
    frequency_signal: 'first_occurrence',
    classification_gate1: 'pending',
    classification_gate2: 'pending',
    dissent_notes: '',
    resolution_owner: '',
    resolution_timeline: '',
    resolution_success_criteria: '',
    gate1_classified_at: '',
    gate2_classified_at: '',
    classified_by: '',
    classified_at: ''
  });

  // Extract initial tags from issue
  const initialTags = issue ? {
    tagged_officers: issue.tagged_officers || [],
    tagged_artifacts: issue.tagged_artifacts || [],
    tagged_rituals: issue.tagged_rituals || [],
    tagged_policies: issue.tagged_policies || [],
    tagged_capabilities: issue.tagged_capabilities || []
  } : null;

  // Use the unified tagging hook
  const tagging = useEntityTagging({
    officers,
    artifacts,
    rituals,
    policies,
    capabilities,
    currentUserEmail,
    initialTags
  });

  useEffect(() => {
    if (issue) {
      let linkedMis = issue.linked_mission_id || '';
      let linkedObj = issue.linked_objective_id || '';
      let linkedAct = issue.linked_activity_id || '';

      // If issue is from activity report, prepopulate based on source activity
      if (issue.source === 'activity' && issue.activity_id) {
        linkedAct = issue.activity_id;
        const activity = activities?.find(a => a.id === issue.activity_id);
        if (activity) {
          linkedObj = activity.objective_id;
          const objective = objectives?.find(o => o.id === activity.objective_id);
          if (objective) {
            linkedMis = objective.mission_id;
          }
        }
      }

      setFormData({
        title: issue.title || '',
        description: issue.description || '',
        category: issue.category || 'execution_headwind',
        severity: issue.severity || 'medium',
        linked_mission_id: linkedMis,
        linked_objective_id: linkedObj,
        linked_activity_id: linkedAct,
        decision: issue.decision || '',
        decision_notes: issue.decision_notes || '',
        observed_impact: issue.observed_impact || '',
        frequency_signal: issue.frequency_signal || 'first_occurrence',
        classification_gate1: issue.classification_gate1 || 'pending',
        classification_gate2: issue.classification_gate2 || 'pending',
        dissent_notes: issue.dissent_notes || '',
        resolution_owner: issue.resolution_owner || '',
        resolution_timeline: issue.resolution_timeline || '',
        resolution_success_criteria: issue.resolution_success_criteria || '',
        gate1_classified_at: issue.gate1_classified_at || '',
        gate2_classified_at: issue.gate2_classified_at || '',
        classified_by: issue.classified_by || '',
        classified_at: issue.classified_at || ''
      });

      // Reset tags when issue changes
      tagging.setTags({
        tagged_officers: issue.tagged_officers || [],
        tagged_artifacts: issue.tagged_artifacts || [],
        tagged_rituals: issue.tagged_rituals || [],
        tagged_policies: issue.tagged_policies || [],
        tagged_capabilities: issue.tagged_capabilities || []
      });
    } else {
      setFormData({
        title: '',
        description: '',
        category: 'execution_headwind',
        severity: 'medium',
        linked_mission_id: '',
        linked_objective_id: '',
        linked_activity_id: '',
        decision: '',
        decision_notes: '',
        observed_impact: '',
        frequency_signal: 'first_occurrence',
        classification_gate1: 'pending',
        classification_gate2: 'pending',
        dissent_notes: '',
        resolution_owner: '',
        resolution_timeline: '',
        resolution_success_criteria: '',
        gate1_classified_at: '',
        gate2_classified_at: '',
        classified_by: '',
        classified_at: ''
      });
      tagging.resetTags();
    }
  }, [issue, activities, objectives]);

  const createIssueMutation = useMutation({
    mutationFn: (data) => base44.entities.Issue.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      onClose();
    }
  });

  const updateIssueMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const updateData = { ...data };
      if (data.decision && !issue.decision_made_by) {
        const user = await base44.auth.me();
        updateData.decision_made_by = user.email;
        updateData.decision_made_at = new Date().toISOString();
        updateData.status = 'decision_made';
      }
      return base44.entities.Issue.update(id, updateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      queryClient.invalidateQueries({ queryKey: ['reportIssues'] });
      onClose();
    }
  });

  const handleClassificationChange = (field, value) => {
    const now = new Date().toISOString();
    const updates = { [field]: value, classified_by: currentUserEmail, classified_at: now };
    if (field === 'classification_gate1') {
      updates.gate1_classified_at = now;
      if (value === 'noise') {
        updates.classification_gate2 = 'pending';
        updates.gate2_classified_at = '';
      }
    }
    if (field === 'classification_gate2') {
      updates.gate2_classified_at = now;
    }
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = { ...formData, ...tagging.tags };
    
    if (issue) {
      updateIssueMutation.mutate({ id: issue.id, data: submitData });
    } else {
      const user = await base44.auth.me();
      createIssueMutation.mutate({
        ...submitData,
        reported_by: user.email,
        reported_at: new Date().toISOString(),
        status: 'open'
      });
    }
  };

  const showResolutionSection = formData.classification_gate2 === 'balloon' || formData.classification_gate2 === 'rock';

  return (
    <ConfirmableDialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="issues-issuedialog-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{issue ? 'Edit Issue' : 'Report New Issue'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-4">
          
          {/* SECTION: Basic Information */}
          <CollapsibleSection title="Basic Information">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input data-testid="issues-issuedialog-input"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea data-testid="issues-issuedialog-textarea"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger className="bg-slate-950 border-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="red_line_violation">Red Line Violation</SelectItem>
                    <SelectItem value="execution_headwind">Execution Headwind</SelectItem>
                    <SelectItem value="capability_gap">Capability Gap</SelectItem>
                    <SelectItem value="process_friction">Process Friction</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Severity</Label>
                <Select value={formData.severity} onValueChange={(value) => setFormData({ ...formData, severity: value })}>
                  <SelectTrigger className="bg-slate-950 border-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CollapsibleSection>

          {/* SECTION: Observed Impact & Frequency */}
          <CollapsibleSection title="Observed Impact & Frequency">
            <div className="space-y-2">
              <Label>Observed Impact</Label>
              <Textarea data-testid="issues-issuedialog-textarea-1"
                value={formData.observed_impact}
                onChange={(e) => setFormData({ ...formData, observed_impact: e.target.value })}
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                rows={3}
                placeholder="What was the immediate effect? Was something blocked, slowed, degraded, or failed?"
              />
            </div>
            <div className="space-y-2">
              <Label>Frequency Signal</Label>
              <Select value={formData.frequency_signal} onValueChange={(value) => setFormData({ ...formData, frequency_signal: value })}>
                <SelectTrigger className="bg-slate-950 border-slate-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="first_occurrence">First Occurrence</SelectItem>
                  <SelectItem value="recurring">Recurring</SelectItem>
                  <SelectItem value="escalating">Escalating</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CollapsibleSection>

          {/* SECTION: Hierarchy & Tags */}
          <CollapsibleSection title="Hierarchy & Tags">
            <div className="space-y-2">
              <Label>Allocate to Mission</Label>
              <SearchableTagSelect
                options={(missions || []).map((mission) => ({
                  value: mission.id,
                  label: `[${mission.mission_code || 'No Code'}] ${mission.title}`
                }))}
                value={formData.linked_mission_id}
                onValueChange={(value) => setFormData({ ...formData, linked_mission_id: value, linked_objective_id: '', linked_activity_id: '' })}
                placeholder="Select mission..."
                emptyMessage="No missions found."
                disabled={issue && issue.source === 'activity'}
              />
            </div>

            <div className="space-y-2">
              <Label>Allocate to Objective</Label>
              <SearchableTagSelect
                options={(objectives || [])
                  .filter(objective => !formData.linked_mission_id || objective.mission_id === formData.linked_mission_id)
                  .map((objective) => ({
                    value: objective.id,
                    label: `[${objective.objective_code}] ${objective.statement?.substring(0, 60) || ''}...`
                  }))}
                value={formData.linked_objective_id}
                onValueChange={(value) => setFormData({ ...formData, linked_objective_id: value, linked_activity_id: '' })}
                placeholder={formData.linked_mission_id ? "Select objective..." : "Select mission first..."}
                emptyMessage="No objectives found."
                disabled={issue && issue.source === 'activity'}
              />
            </div>

            <div className="space-y-2">
              <Label>Allocate to Activity</Label>
              <SearchableTagSelect
                options={(activities || [])
                  .filter(activity => !formData.linked_objective_id || activity.objective_id === formData.linked_objective_id)
                  .map((activity) => ({
                    value: activity.id,
                    label: `[${activity.activity_code || 'No Code'}] ${activity.title}`
                  }))}
                value={formData.linked_activity_id}
                onValueChange={(value) => setFormData({ ...formData, linked_activity_id: value })}
                placeholder={formData.linked_objective_id ? "Select activity..." : "Select objective first..."}
                emptyMessage="No activities found."
                disabled={issue && issue.source === 'activity'}
              />
            </div>

            <div className="space-y-2">
              <Label>Tags</Label>
              <EntityTagInput
                tags={tagging.tags}
                onAddTag={tagging.addTag}
                onRemoveTag={tagging.removeTag}
                getTagOptions={tagging.getTagOptions}
                currentTagType={tagging.currentTagType}
                onTagTypeChange={tagging.setCurrentTagType}
                officers={officers}
              />
            </div>
          </CollapsibleSection>

          {/* SECTION: High Council Classification (Commander only) */}
          {isCommander && issue && (
            <CollapsibleSection title={<span className="flex items-center gap-2"><Shield className="w-4 h-4 text-blue-400" /> High Council Classification</span>} defaultOpen={formData.classification_gate1 !== 'pending'}>
              <div className="rounded-lg border border-blue-500/30 bg-blue-950/30 p-4 space-y-4">
                <div className="space-y-2">
                  <Label className="text-blue-300">Gate 1: Noise vs. Signal</Label>
                  <Select value={formData.classification_gate1} onValueChange={(value) => handleClassificationChange('classification_gate1', value)}>
                    <SelectTrigger className="bg-slate-950 border-blue-500/30">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="noise">Noise — No action needed</SelectItem>
                      <SelectItem value="signal">Signal — Real friction</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.classification_gate1 === 'noise' && (
                  <div className="space-y-2">
                    <Label className="text-blue-300">Dissent Notes</Label>
                    <Textarea data-testid="issues-issuedialog-textarea-2"
                      value={formData.dissent_notes}
                      onChange={(e) => setFormData({ ...formData, dissent_notes: e.target.value })}
                      className="bg-slate-950 border-blue-500/30 text-slate-200 placeholder:text-slate-500"
                      rows={2}
                      placeholder="Record any dissenting views from the council..."
                    />
                  </div>
                )}

                {formData.classification_gate1 === 'signal' && (
                  <div className="space-y-2">
                    <Label className="text-blue-300">Gate 2: Density (Balloon vs. Rock)</Label>
                    <Select value={formData.classification_gate2} onValueChange={(value) => handleClassificationChange('classification_gate2', value)}>
                      <SelectTrigger className="bg-slate-950 border-blue-500/30">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="balloon">🎈 Balloon — Low-Density (existing capabilities)</SelectItem>
                        <SelectItem value="rock">🪨 Rock — High-Density (requires new capabilities)</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.classification_gate2 !== 'pending' && (
                      <div className="space-y-2 mt-2">
                        <Label className="text-blue-300">Dissent Notes</Label>
                        <Textarea data-testid="issues-issuedialog-textarea-3"
                          value={formData.dissent_notes}
                          onChange={(e) => setFormData({ ...formData, dissent_notes: e.target.value })}
                          className="bg-slate-950 border-blue-500/30 text-slate-200 placeholder:text-slate-500"
                          rows={2}
                          placeholder="Record any dissenting views from the council..."
                        />
                      </div>
                    )}
                  </div>
                )}

                {formData.classified_by && (
                  <p className="text-xs text-blue-400/60">
                    Last classified by {officers?.find(o => o.user_email === formData.classified_by)?.callsign || formData.classified_by}
                    {formData.classified_at && ` on ${new Date(formData.classified_at).toLocaleDateString()}`}
                  </p>
                )}
              </div>
            </CollapsibleSection>
          )}

          {/* SECTION: Resolution Assignment */}
          {isCommander && issue && showResolutionSection && (
            <CollapsibleSection title="Resolution Assignment" defaultOpen={true}>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Resolution Owner</Label>
                  <SearchableTagSelect
                    options={(officers || []).map((o) => ({
                      value: o.user_email,
                      label: `${o.callsign} (${o.rank_title})`
                    }))}
                    value={formData.resolution_owner}
                    onValueChange={(value) => setFormData({ ...formData, resolution_owner: value })}
                    placeholder="Assign resolution owner..."
                    emptyMessage="No officers found."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Resolution Timeline</Label>
                  <Input data-testid="issues-issuedialog-input-1"
                    type="date"
                    value={formData.resolution_timeline}
                    onChange={(e) => setFormData({ ...formData, resolution_timeline: e.target.value })}
                    className="bg-white border-slate-300 text-slate-900"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Success Criteria</Label>
                  <Textarea data-testid="issues-issuedialog-textarea-4"
                    value={formData.resolution_success_criteria}
                    onChange={(e) => setFormData({ ...formData, resolution_success_criteria: e.target.value })}
                    className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                    rows={3}
                    placeholder="What does 'resolved' look like for this issue?"
                  />
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* Make Decision section */}
          {issue && (issue.status === 'open' || issue.status === 'under_review') && (
            <div className="border-t border-slate-800 pt-4">
              <h3 className="text-lg font-semibold mb-4">
                {formData.classification_gate2 === 'balloon' ? '🎈 Balloon Resolution' : 'Make Decision'}
              </h3>
              <div className="space-y-2">
                <Label>Decision</Label>
                <Select value={formData.decision} onValueChange={(value) => setFormData({ ...formData, decision: value })}>
                  <SelectTrigger className="bg-slate-900 border-slate-800">
                    <SelectValue placeholder="Select decision..." />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.classification_gate2 === 'balloon' ? (
                      <>
                        <SelectItem value="reassign">Reassign / Redistribute</SelectItem>
                        <SelectItem value="process_adjustment">Process Adjustment</SelectItem>
                        <SelectItem value="policy_tweak">Policy Tweak</SelectItem>
                        <SelectItem value="communication_fix">Communication Fix</SelectItem>
                        <SelectItem value="deprioritize_monitor">Deprioritize and Monitor</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="cease_activity">Cease the Activity</SelectItem>
                        <SelectItem value="design_policy">Design a Policy</SelectItem>
                        <SelectItem value="contract_capability">Contract or Acquire Capability</SelectItem>
                        <SelectItem value="develop_capability">Develop New Capability</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {formData.decision && (
                <div className="space-y-4 mt-4">
                  {formData.classification_gate2 === 'balloon' && (
                    <>
                      <div className="space-y-2">
                        <Label>Resolution Owner</Label>
                        <SearchableTagSelect
                          options={(officers || []).map((o) => ({
                            value: o.user_email,
                            label: `${o.callsign} (${o.rank_title})`
                          }))}
                          value={formData.resolution_owner}
                          onValueChange={(value) => setFormData({ ...formData, resolution_owner: value })}
                          placeholder="Assign resolution owner..."
                          emptyMessage="No officers found."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Resolution Timeline</Label>
                        <Input data-testid="issues-issuedialog-input-2"
                          type="date"
                          value={formData.resolution_timeline}
                          onChange={(e) => setFormData({ ...formData, resolution_timeline: e.target.value })}
                          className="bg-white border-slate-300 text-slate-900"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Success Criteria</Label>
                        <Textarea data-testid="issues-issuedialog-textarea-5"
                          value={formData.resolution_success_criteria}
                          onChange={(e) => setFormData({ ...formData, resolution_success_criteria: e.target.value })}
                          className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                          rows={2}
                          placeholder="What does 'resolved' look like?"
                        />
                      </div>
                    </>
                  )}
                  <div className="space-y-2">
                    <Label>Decision Notes</Label>
                    <Textarea data-testid="issues-issuedialog-textarea-6"
                      value={formData.decision_notes}
                      onChange={(e) => setFormData({ ...formData, decision_notes: e.target.value })}
                      className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                      rows={3}
                      placeholder="Explain the decision and next steps..."
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button data-testid="issues-cancel-btn" type="button" variant="outline" onClick={onClose} className="text-slate-900">
              Cancel
            </Button>
            <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {issue ? 'Update Issue' : 'Report Issue'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </ConfirmableDialog>
  );
}